import {
	MouseEvent,
	PropsWithChildren,
	ReactElement,
	useCallback,
	useMemo
} from "react";
import classNames from "classnames";
import styles from "./CalendarLayout.module.less";
import { setTrackingId } from "client/Misc/utils";
import { Coordinate } from "client/Misc/commonTypes";
import { TimeOnly } from "client/DateAndTime/timeOnly";

type CalendarLayoutProps = {
	trackingId: string;
};

export const CalendarLayout = (props: PropsWithChildren<CalendarLayoutProps>): ReactElement => {
	const {
		trackingId,
		children
	} = props;

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.root}
		>
			{children}
		</div>
	);
};
CalendarLayout.displayName = "CalendarLayout";

type CalendarLayoutColumnProps = {
	widthKind?: CalendarLayoutColumnWidthKind;
	isToday?: boolean;
	onClick?: (coordinate: Coordinate) => void;
	trackingId: string;
};

const CalendarLayoutColumn = (props: PropsWithChildren<CalendarLayoutColumnProps>): ReactElement => {
	const {
		widthKind = "default",
		isToday = false,
		onClick,
		children,
		trackingId
	} = props;

	const clickHandler = useCallback(
		(e: MouseEvent): void => {
			onClick?.({
				x: e.clientX,
				y: e.clientY
			});
		},
		[onClick]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.column,
				widthKindToStyleMap[widthKind],
				{
					[styles.column_isToday]: isToday
				}
			)}
			onClick={clickHandler}
		>
			{children}
		</div>
	);
};
CalendarLayoutColumn.displayName = "CalendarLayoutColumn";

type CalendarLayoutHourBoxProps = {
	onClick: (
		coordinate: Coordinate,
		time: TimeOnly
	) => void;
	hourOfTheDay: number;
} | {
	onClick?: undefined;
};

const CalendarLayoutHourBox = (props: PropsWithChildren<CalendarLayoutHourBoxProps>): ReactElement => {
	const {
		children
	} = props;

	const clickHandler = useMemo(
		(): undefined | ((e: MouseEvent) => void) => {
			if (props.onClick) {
				return (e: MouseEvent) => {
					const rect = e.currentTarget.getBoundingClientRect();
					const y = e.clientY - rect.top;
					const shouldAddHalfHour = y > rect.height / 2;

					const time = new TimeOnly(
						props.hourOfTheDay,
						shouldAddHalfHour ? 30 : 0,
						0,
						0
					);

					props.onClick(
						{
							x: e.clientX,
							y: e.clientY
						},
						time
					);
				};
			}

			return undefined;
		},
		[props]
	);

	return (
		<div
			onClick={clickHandler}
			className={styles.hourContainer}
		>
			{children}
		</div>
	);
};
CalendarLayoutHourBox.displayName = "CalendarLayoutHourBox";


type CalendarLayoutColumnWidthKind = "default" | "auto";

export const widthKindToStyleMap: { [key in CalendarLayoutColumnWidthKind]: string | null; } = {
	["default"]: null,
	["auto"]: styles.column_auto
};

CalendarLayout.Column = CalendarLayoutColumn;
CalendarLayout.HourBox = CalendarLayoutHourBox;